import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { 
  getAdminWriters,
  getWriterDetails,
  getWriterOrders,
  approveWriterAccount,
  rejectWriterAccount,
  suspendWriterAccount,
  reactivateWriterAccount,
  updateWriterAdminNotes,
  getWriterEarnings,
  bulkProcessPayments,
  processPaymentForWriter
} from '../api/api';
import { 
  Container, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Box,
  IconButton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardHeader,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Edit,
  Refresh,
  Assignment,
  Payment,
  AccountCircle,
  Email,
  Phone,
  LocationOn,
  School,
  Work,
  AttachMoney,
  Star,
  AccessTime,
  PersonAdd,
  Block,
  Check,
  Close,
  Warning,
  MoreVert,
  Comment,
  History,
  BugReport,
  ExpandMore,
  Search,
  FilterList,
  Download,
  Upload,
  PersonRemove,
  AutoFixHigh,
  DataArray,
  CalendarToday,
  Language,
  Description,
  Timer,
  Business,
  Home,
  Public,
  WorkHistory,
  AccountBalance,
  Translate,
  Subject,
  School as SchoolIcon,
  Psychology,
  Computer,
  History as HistoryIcon,
  MenuBook,
  Link,
  Web,
  LinkedIn,
  Settings,
  AdminPanelSettings,
  VerifiedUser,
  GppGood,
  GppBad,
  Lock,
  VpnKey,
  HowToReg,
  PersonOff,
  PersonOutline,
  Person,
  Group,
  People,
  ManageAccounts,
  AccountBox,
  Badge as BadgeIcon,
  ContactMail,
  PermIdentity,
  AssignmentInd,
  CardMembership,
  CreditCard,
  AccountBalanceWallet,
  MonetizationOn,
  Payments,
  PriceCheck,
  CurrencyExchange,
  Euro,
  Money,
  Receipt,
  Article,
  Drafts,
  Note,
  TextSnippet,
  BorderColor,
  Create,
  EditNote,
  AutoFixNormal,
  RocketLaunch,
  TrendingUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`writers-tabpanel-${index}`}
      aria-labelledby={`writers-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// DataCard component for displaying key-value pairs
const DataCard = ({ title, value, icon: Icon, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Icon color={color} sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h6" component="div">
        {value || 'N/A'}
      </Typography>
    </CardContent>
  </Card>
);

// SectionHeader component
const SectionHeader = ({ title, icon: Icon, color = 'primary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
    <Icon color={color} sx={{ mr: 1 }} />
    <Typography variant="h6" component="div">
      {title}
    </Typography>
  </Box>
);

const ManageWriters = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State
  const [writers, setWriters] = useState([]);
  const [filteredWriters, setFilteredWriters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedWriter, setSelectedWriter] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [bulkPaymentDialogOpen, setBulkPaymentDialogOpen] = useState(false);
  
  // Form states
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState('paypal');
  const [bulkTransactionId, setBulkTransactionId] = useState('');
  const [selectedPayments, setSelectedPayments] = useState([]);
  
  // Tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Earnings state
  const [writerEarnings, setWriterEarnings] = useState([]);
  const [pendingEarnings, setPendingEarnings] = useState([]);
  const [processedEarnings, setProcessedEarnings] = useState([]);

  // Orders state
  const [writerOrders, setWriterOrders] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState({
    total_orders: 0,
    completed_orders: 0,
    in_progress_orders: 0,
    cancelled_orders: 0,
    total_earnings: 0,
    average_rating: 0,
    total_ratings: 0,
  });

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    professional: true,
    education: true,
    experience: true,
    preferences: true,
    orders: true,
    performance: true,
    admin: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'ADMIN') {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch writers on component mount
  useEffect(() => {
    fetchWriters();
  }, []);

  // Filter writers based on status and search
  useEffect(() => {
    let filtered = writers;
    
    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(writer => writer.account_status === statusFilter);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(writer => 
        writer.user?.username?.toLowerCase().includes(term) ||
        writer.user?.email?.toLowerCase().includes(term) ||
        writer.first_name?.toLowerCase().includes(term) ||
        writer.last_name?.toLowerCase().includes(term) ||
        writer.country?.toLowerCase().includes(term) ||
        writer.phone_number?.toLowerCase().includes(term) ||
        writer.professional_title?.toLowerCase().includes(term) ||
        writer.highest_degree?.toLowerCase().includes(term) ||
        writer.field_of_study?.toLowerCase().includes(term) ||
        writer.university?.toLowerCase().includes(term)
      );
    }
    
    setFilteredWriters(filtered);
  }, [writers, statusFilter, searchTerm]);

  // Update pending payments when tab changes
  useEffect(() => {
    if (tabValue === 1) {
      calculatePendingPayments();
    }
  }, [tabValue, writers]);

  const fetchWriters = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('👥 Fetching writer profiles...');
      const data = await getAdminWriters();
      
      console.log('📊 Data received from API:', data);
      
      if (!data || data.length === 0) {
        console.log('ℹ️ No writer profiles found');
        setWriters([]);
        setFilteredWriters([]);
        return;
      }
      
      // Sort writers: PENDING and UNDER_REVIEW first, then by creation date
      const sortedWriters = [...data].sort((a, b) => {
        // Priority for pending review
        const aStatus = a.account_status || 'PENDING';
        const bStatus = b.account_status || 'PENDING';
        
        if (aStatus === 'PENDING' && bStatus !== 'PENDING') return -1;
        if (bStatus === 'PENDING' && aStatus !== 'PENDING') return 1;
        
        // Then under review
        if (aStatus === 'UNDER_REVIEW' && bStatus !== 'UNDER_REVIEW') return -1;
        if (bStatus === 'UNDER_REVIEW' && aStatus !== 'UNDER_REVIEW') return 1;
        
        // Then by creation date (newest first)
        const aDate = a.account_created_at;
        const bDate = b.account_created_at;
        if (aDate && bDate) {
          return new Date(bDate) - new Date(aDate);
        }
        return 0;
      });
      
      console.log(`✅ Found and sorted ${sortedWriters.length} writer profiles`);
      
      setWriters(sortedWriters);
      setFilteredWriters(sortedWriters);
      
    } catch (err) {
      console.error('❌ Error fetching writers:', err);
      setError('Failed to load writers. Please try again.');
      showSnackbar('Failed to load writers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchWriterEarnings = async (writerId) => {
    try {
      console.log(`💰 Fetching earnings for writer ${writerId}...`);
      const earnings = await getWriterEarnings(writerId);
      setWriterEarnings(earnings);
      setPendingEarnings(earnings.filter(e => e.payment_status === 'PENDING'));
      setProcessedEarnings(earnings.filter(e => e.payment_status === 'PAID'));
    } catch (err) {
      console.error('Error fetching earnings:', err);
      // Use dummy data if API not ready
      const dummyEarnings = [
        {
          id: 1,
          order_id: 101,
          order_total: 150.00,
          writer_amount: 30.00,
          payment_status: 'PAID',
          paid_at: '2024-01-15T10:30:00Z',
          payment_method: 'PayPal'
        },
        {
          id: 2,
          order_id: 102,
          order_total: 200.00,
          writer_amount: 40.00,
          payment_status: 'PENDING',
          created_at: '2024-01-20T14:45:00Z'
        }
      ];
      setWriterEarnings(dummyEarnings);
      setPendingEarnings(dummyEarnings.filter(e => e.payment_status === 'PENDING'));
      setProcessedEarnings(dummyEarnings.filter(e => e.payment_status === 'PAID'));
    }
  };

  const fetchWriterOrders = async (writerId) => {
    try {
      console.log(`📋 Fetching orders for writer ${writerId}...`);
      const ordersData = await getWriterOrders(writerId);
      
      console.log('📊 Orders data received:', ordersData);
      
      // Update orders and summary
      setWriterOrders(ordersData.orders || []);
      setOrdersSummary(ordersData.summary || {
        total_orders: 0,
        completed_orders: 0,
        in_progress_orders: 0,
        cancelled_orders: 0,
        total_earnings: 0,
        average_rating: 0,
        total_ratings: 0,
      });
      
      console.log(`✅ Found ${ordersData.orders?.length || 0} orders for writer`);
      
    } catch (err) {
      console.error('Error fetching writer orders:', err);
      
      // Use mock data based on your screenshot
      const mockOrders = [
        {
          id: 83,
          title: 'Write About Joseck Mogusu',
          client: { 
            username: 'joseck_mogusu1', 
            email: 'joseckmogusu20152@gmail.com' 
          },
          pages: 1230,
          budget: 55104.00,
          deadline: '2026-02-09T19:13:00Z',
          status: 'CLOSED',
          created_at: '2026-02-07T15:11:00Z',
          completed_at: '2026-02-07T15:16:00Z',
          writer_earning: 11020.80, // 20% of 55104
          rating: 4,
          review: 'Done'
        },
        {
          id: 82,
          title: 'Taita',
          client: { username: 'joseck_mogusu1' },
          pages: 1234,
          budget: 43190.00,
          deadline: '2026-02-21T03:43:00Z',
          status: 'CLOSED',
          created_at: '2026-02-07T15:00:00Z',
          completed_at: '2026-02-07T15:10:00Z',
          writer_earning: 8638.00, // 20% of 43190
        },
        {
          id: 81,
          title: 'Essay Help',
          client: { username: 'joseck_mogusu1' },
          pages: 100,
          budget: 1000.00,
          deadline: '2026-02-14T00:31:00Z',
          status: 'CLOSED',
          created_at: '2026-02-07T14:50:00Z',
          completed_at: '2026-02-07T15:00:00Z',
          writer_earning: 200.00, // 20% of 1000
        },
        {
          id: 80,
          title: 'pato',
          client: { username: 'joseck_mogusu1' },
          pages: 1,
          budget: 30.00,
          deadline: '2026-02-28T18:57:00Z',
          status: 'CLOSED',
          created_at: '2026-02-07T14:40:00Z',
          completed_at: '2026-02-07T14:45:00Z',
          writer_earning: 6.00, // 20% of 30
        },
        {
          id: 79,
          title: 'ksdsddjsdjksjksdjk',
          client: { username: 'joseck_mogusu1' },
          pages: 1233,
          budget: 18495.00,
          deadline: '2026-02-28T20:05:00Z',
          status: 'CLOSED',
          created_at: '2026-02-07T14:30:00Z',
          completed_at: '2026-02-07T14:35:00Z',
          writer_earning: 3699.00, // 20% of 18495
        }
      ];
      
      setWriterOrders(mockOrders);
      
      // Calculate summary from mock data
      const totalEarnings = mockOrders.reduce((sum, order) => sum + (order.writer_earning || 0), 0);
      const completedOrders = mockOrders.filter(o => o.status === 'CLOSED' || o.status === 'COMPLETED').length;
      const ratings = mockOrders.filter(o => o.rating).map(o => o.rating);
      const averageRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
      
      setOrdersSummary({
        total_orders: mockOrders.length,
        completed_orders: completedOrders,
        in_progress_orders: 0,
        cancelled_orders: 0,
        total_earnings: totalEarnings,
        average_rating: averageRating,
        total_ratings: ratings.length,
      });
    }
  };

  const calculatePendingPayments = () => {
    const pendingPayments = writers
      .filter(w => w.pending_earnings > 0)
      .map(w => ({
        writer_id: w.id,
        writer_name: w.full_name || w.user?.username,
        writer_username: w.user?.username,
        writer_email: w.user?.email,
        pending_amount: w.pending_earnings || 0,
        account_status: w.account_status,
        profile_created_at: w.account_created_at,
        last_payment_at: null // You can add logic to track this
      }));
    
    console.log(`💰 Calculated ${pendingPayments.length} pending payments from writer profiles`);
    return pendingPayments;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewWriter = async (writer) => {
    try {
      console.log(`🔍 Fetching details for writer ${writer.id}...`);
      
      // Fetch detailed writer info
      const detailedWriter = await getWriterDetails(writer.id);
      setSelectedWriter(detailedWriter);
      setViewDialogOpen(true);
      
      // Fetch writer's orders
      await fetchWriterOrders(writer.id);
      
      // Fetch earnings for this writer
      fetchWriterEarnings(writer.id);
      
    } catch (err) {
      console.error('Error fetching writer details:', err);
      // Use the basic writer info if details fail
      setSelectedWriter(writer);
      setViewDialogOpen(true);
      await fetchWriterOrders(writer.id);
    }
  };

  const handleApproveWriter = async (writer) => {
    try {
      console.log(`✅ Approving writer ${writer.id}...`);
      await approveWriterAccount(writer.id);
      
      // Update local state
      const updatedWriters = writers.map(w => 
        w.id === writer.id 
          ? { 
              ...w, 
              account_status: 'APPROVED',
              approved_at: new Date().toISOString(),
            } 
          : w
      );
      
      setWriters(updatedWriters);
      if (selectedWriter && selectedWriter.id === writer.id) {
        setSelectedWriter({...selectedWriter, account_status: 'APPROVED'});
      }
      
      showSnackbar(`Writer ${writer.user?.username || writer.id} approved successfully!`, 'success');
      
      // Close any open dialogs
      setViewDialogOpen(false);
      
    } catch (err) {
      console.error('Error approving writer:', err);
      showSnackbar('Failed to approve writer: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleRejectWriter = async (writer) => {
    try {
      if (!rejectionReason.trim()) {
        showSnackbar('Please provide a rejection reason', 'warning');
        return;
      }
      
      console.log(`❌ Rejecting writer ${writer.id}...`);
      await rejectWriterAccount(writer.id, rejectionReason);
      
      // Update local state
      const updatedWriters = writers.map(w => 
        w.id === writer.id 
          ? { 
              ...w, 
              account_status: 'REJECTED',
              rejection_reason: rejectionReason,
            } 
          : w
      );
      
      setWriters(updatedWriters);
      if (selectedWriter && selectedWriter.id === writer.id) {
        setSelectedWriter({...selectedWriter, account_status: 'REJECTED', rejection_reason: rejectionReason});
      }
      
      showSnackbar(`Writer ${writer.user?.username || writer.id} has been rejected`, 'info');
      
      // Reset form and close dialogs
      setRejectionReason('');
      setRejectDialogOpen(false);
      setViewDialogOpen(false);
      
    } catch (err) {
      console.error('Error rejecting writer:', err);
      showSnackbar('Failed to reject writer: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleSuspendWriter = async (writer) => {
    try {
      console.log(`⚠️ Suspending writer ${writer.id}...`);
      await suspendWriterAccount(writer.id);
      
      // Update local state
      const updatedWriters = writers.map(w => 
        w.id === writer.id 
          ? { 
              ...w, 
              account_status: 'SUSPENDED',
            } 
          : w
      );
      
      setWriters(updatedWriters);
      if (selectedWriter && selectedWriter.id === writer.id) {
        setSelectedWriter({...selectedWriter, account_status: 'SUSPENDED'});
      }
      
      showSnackbar(`Writer ${writer.user?.username || writer.id} has been suspended`, 'warning');
      
      setViewDialogOpen(false);
      
    } catch (err) {
      console.error('Error suspending writer:', err);
      showSnackbar('Failed to suspend writer: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleReactivateWriter = async (writer) => {
    try {
      console.log(`🔄 Reactivating writer ${writer.id}...`);
      await reactivateWriterAccount(writer.id);
      
      // Update local state
      const updatedWriters = writers.map(w => 
        w.id === writer.id 
          ? { 
              ...w, 
              account_status: 'APPROVED',
            } 
          : w
      );
      
      setWriters(updatedWriters);
      if (selectedWriter && selectedWriter.id === writer.id) {
        setSelectedWriter({...selectedWriter, account_status: 'APPROVED'});
      }
      
      showSnackbar(`Writer ${writer.user?.username || writer.id} has been reactivated`, 'success');
      
      setViewDialogOpen(false);
      
    } catch (err) {
      console.error('Error reactivating writer:', err);
      showSnackbar('Failed to reactivate writer: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleUpdateAdminNotes = async (writer) => {
    try {
      if (!adminNotes.trim()) {
        showSnackbar('Please enter admin notes', 'warning');
        return;
      }
      
      console.log(`📝 Updating admin notes for writer ${writer.id}...`);
      await updateWriterAdminNotes(writer.id, adminNotes);
      
      // Update local state
      const updatedWriters = writers.map(w => 
        w.id === writer.id 
          ? { 
              ...w, 
              admin_notes: adminNotes
            } 
          : w
      );
      
      setWriters(updatedWriters);
      if (selectedWriter && selectedWriter.id === writer.id) {
        setSelectedWriter({...selectedWriter, admin_notes: adminNotes});
      }
      
      showSnackbar('Admin notes updated successfully', 'success');
      
      setNotesDialogOpen(false);
      setAdminNotes('');
      
    } catch (err) {
      console.error('Error updating notes:', err);
      showSnackbar('Failed to update notes: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleProcessPayment = async () => {
    try {
      if (!selectedWriter || paymentAmount <= 0) {
        showSnackbar('Please select a writer and enter amount', 'warning');
        return;
      }
      
      if (!paymentMethod) {
        showSnackbar('Please select payment method', 'warning');
        return;
      }
      
      // Calculate new earnings
      const payment = parseFloat(paymentAmount);
      const currentPending = selectedWriter.pending_earnings || 0;
      
      if (payment > currentPending) {
        showSnackbar(`Payment amount exceeds pending earnings ($${currentPending.toFixed(2)})`, 'warning');
        return;
      }
      
      // Process payment through API
      console.log(`💰 Processing payment for writer ${selectedWriter.id}...`);
      await processPaymentForWriter(selectedWriter.id, {
        amount: payment,
        payment_method: paymentMethod,
        transaction_id: `PAY-${selectedWriter.id}-${Date.now()}`,
        notes: paymentNotes
      });
      
      // Refresh data from backend
      await fetchWriters();
      
      showSnackbar(`Payment of $${paymentAmount} processed for ${selectedWriter.full_name || selectedWriter.user?.username}`, 'success');
      
      // Close dialog and reset
      setPaymentDialogOpen(false);
      setPaymentAmount(0);
      setSelectedWriter(null);
      setPaymentNotes('');
      
    } catch (err) {
      console.error('Error processing payment:', err);
      showSnackbar('Failed to process payment: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleBulkPaymentSelect = (writerId) => {
    setSelectedPayments(prev => {
      if (prev.includes(writerId)) {
        return prev.filter(id => id !== writerId);
      } else {
        return [...prev, writerId];
      }
    });
  };

  // FIXED: Bulk payment function that actually calls the backend API
  const handleBulkPaymentApprove = async () => {
    try {
      if (selectedPayments.length === 0) {
        showSnackbar('Please select writers to pay', 'warning');
        return;
      }
      
      if (!bulkPaymentMethod) {
        showSnackbar('Please select payment method', 'warning');
        return;
      }
      
      console.log(`💰 Processing bulk payment for ${selectedPayments.length} writers...`);
      
      // Calculate total to pay and create payment data
      const writersToPay = writers.filter(w => selectedPayments.includes(w.id) && w.pending_earnings > 0);
      const totalPaid = writersToPay.reduce((sum, w) => sum + (w.pending_earnings || 0), 0);
      
      // Prepare payment data for the API
      const paymentData = {
        writer_ids: selectedPayments,
        payment_method: bulkPaymentMethod,
        transaction_id: bulkTransactionId || `BULK-TXN-${Date.now()}`,
        notes: `Bulk payment processed on ${new Date().toLocaleString()}`,
        writersData: writersToPay.map(w => ({
          id: w.id,
          username: w.user?.username,
          amount: w.pending_earnings || 0
        }))
      };
      
      // OPTION 1: Use the bulk payment API endpoint (if available)
      try {
        console.log('📤 Calling bulk process payments API...');
        const response = await bulkProcessPayments(paymentData);
        console.log('✅ Bulk payment API response:', response);
        
        showSnackbar(`Bulk payment of $${totalPaid.toFixed(2)} processed for ${selectedPayments.length} writers`, 'success');
        
      } catch (apiError) {
        console.warn('⚠️ Bulk API failed, processing individually...', apiError);
        
        // OPTION 2: Fallback - Process each writer individually
        const results = [];
        const errors = [];
        
        for (const writerId of selectedPayments) {
          const writer = writers.find(w => w.id === writerId);
          if (writer && writer.pending_earnings > 0) {
            try {
              // Process individual payment
              await processPaymentForWriter(writerId, {
                amount: writer.pending_earnings,
                payment_method: bulkPaymentMethod,
                transaction_id: `${bulkTransactionId || `TXN${Date.now()}`}-${writerId}`,
                notes: `Bulk payment - ${bulkPaymentMethod}`
              });
              
              results.push({
                writer_id: writerId,
                writer_name: writer.full_name || writer.user?.username,
                amount: writer.pending_earnings
              });
              
            } catch (indError) {
              errors.push({
                writer_id: writerId,
                error: indError.message || 'Payment failed'
              });
              console.error(`❌ Failed to process payment for writer ${writerId}:`, indError);
            }
          }
        }
        
        if (errors.length > 0) {
          showSnackbar(`Processed ${results.length} payments, ${errors.length} failed`, 'warning');
        } else {
          showSnackbar(`All ${results.length} payments processed successfully`, 'success');
        }
      }
      
      // Refresh the writer list to get updated data from backend
      await fetchWriters();
      
      // Reset state
      setSelectedPayments([]);
      setBulkPaymentDialogOpen(false);
      setBulkPaymentMethod('paypal');
      setBulkTransactionId('');
      
    } catch (err) {
      console.error('❌ Error processing bulk payment:', err);
      showSnackbar('Failed to process bulk payment: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'UNDER_REVIEW': return 'info';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'SUSPENDED': return 'default';
      default: return 'default';
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Pending Review';
      case 'UNDER_REVIEW': return 'Under Review';
      case 'APPROVED': return 'Approved';
      case 'REJECTED': return 'Rejected';
      case 'SUSPENDED': return 'Suspended';
      default: return status || 'Unknown';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (err) {
      return dateString;
    }
  };

  // Format date relative
  const formatDateRelative = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (err) {
      return dateString;
    }
  };

  // Calculate completion percentage
  const getCompletionPercentage = (writer) => {
    if (writer.account_status === 'APPROVED') return 100;
    if (writer.registration_stage === 'APPLICATION_SUBMITTED') return 100;
    if (writer.registration_stage === 'PROFILE_COMPLETE') return 66;
    if (writer.registration_stage === 'ACCOUNT_CREATED') return 33;
    return 0;
  };

  // Render array as chips
  const renderArrayAsChips = (array, max = 5) => {
    if (!array || array.length === 0) return 'N/A';
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {array.slice(0, max).map((item, index) => (
          <Chip
            key={index}
            label={item}
            size="small"
            variant="outlined"
          />
        ))}
        {array.length > max && (
          <Chip
            label={`+${array.length - max} more`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    );
  };

  if (loading && writers.length === 0) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading writers...</Typography>
      </Container>
    );
  }

  const pendingPaymentsData = calculatePendingPayments();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Manage Writers
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Review, approve, and manage writer accounts and payments
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Writers
              </Typography>
              <Typography variant="h4">
                {writers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending Review
              </Typography>
              <Typography variant="h4" color="warning.main">
                {writers.filter(w => w.account_status === 'PENDING').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Approved
              </Typography>
              <Typography variant="h4" color="success.main">
                {writers.filter(w => w.account_status === 'APPROVED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending Payments
              </Typography>
              <Typography variant="h4" color="error.main">
                {pendingPaymentsData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Writers"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, username, phone, degree..."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchWriters}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Writer Accounts" />
          <Tab label="Earnings & Payments" />
          <Tab label="Performance Analytics" />
        </Tabs>

        {/* Tab 1: Writer Accounts */}
        <TabPanel value={tabValue} index={0}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {writers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No writer profiles found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Writer profiles will appear here once they are created through the registration process.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Writer</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Registration Date</TableCell>
                      <TableCell>Specialties</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Completion</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredWriters
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((writer) => (
                        <TableRow key={writer.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                {writer.first_name?.[0] || writer.user?.username?.[0] || 'W'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {writer.first_name && writer.last_name 
                                    ? `${writer.first_name} ${writer.last_name}`
                                    : writer.user?.username || 'N/A'
                                  }
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {writer.user?.email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(writer.account_status)}
                              color={getStatusColor(writer.account_status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(writer.account_created_at)}
                          </TableCell>
                          <TableCell>
                            {renderArrayAsChips(writer.specialties, 2)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Star fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                              <Typography>
                                {writer.average_rating > 0 ? writer.average_rating.toFixed(1) : 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={getCompletionPercentage(writer)}
                                  color={
                                    getCompletionPercentage(writer) === 100 
                                      ? 'success' 
                                      : getCompletionPercentage(writer) >= 66 
                                        ? 'primary' 
                                        : 'warning'
                                  }
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {getCompletionPercentage(writer)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewWriter(writer)}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {writer.account_status === 'PENDING' || writer.account_status === 'UNDER_REVIEW' ? (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleApproveWriter(writer)}
                                    >
                                      <Check fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setSelectedWriter(writer);
                                        setRejectDialogOpen(true);
                                      }}
                                    >
                                      <Close fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : writer.account_status === 'APPROVED' ? (
                                <Tooltip title="Suspend">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleSuspendWriter(writer)}
                                  >
                                    <Block fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : writer.account_status === 'SUSPENDED' ? (
                                <Tooltip title="Reactivate">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleReactivateWriter(writer)}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredWriters.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TabPanel>

        {/* Tab 2: Earnings & Payments */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Pending Payments Section */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Pending Payments (Real Data from Writer Profiles)"
                  subheader={`${pendingPaymentsData.length} writers have pending payments totaling $${pendingPaymentsData.reduce((sum, p) => sum + p.pending_amount, 0).toFixed(2)}`}
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={fetchWriters}
                        size="small"
                      >
                        Refresh
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Payment />}
                        onClick={() => setBulkPaymentDialogOpen(true)}
                        disabled={pendingPaymentsData.length === 0}
                        size="small"
                      >
                        Bulk Pay ({selectedPayments.length})
                      </Button>
                    </Box>
                  }
                />
                <Divider />
                {pendingPaymentsData.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Alert severity="info">
                      No pending payments found in writer profiles
                    </Alert>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedPayments.length > 0 && selectedPayments.length < pendingPaymentsData.length}
                              checked={pendingPaymentsData.length > 0 && selectedPayments.length === pendingPaymentsData.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPayments(pendingPaymentsData.map(p => p.writer_id));
                                } else {
                                  setSelectedPayments([]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>Writer</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Pending Amount</TableCell>
                          <TableCell>Member Since</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingPaymentsData.map((payment) => (
                          <TableRow key={payment.writer_id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedPayments.includes(payment.writer_id)}
                                onChange={() => handleBulkPaymentSelect(payment.writer_id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                  {payment.writer_name?.[0] || payment.writer_username?.[0] || 'W'}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {payment.writer_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {payment.writer_email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={payment.account_status}
                                color={payment.account_status === 'APPROVED' ? 'success' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="error">
                                ${payment.pending_amount.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {payment.profile_created_at ? formatDateRelative(payment.profile_created_at) : 'N/A'}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<Payment />}
                                onClick={() => {
                                  const writer = writers.find(w => w.id === payment.writer_id);
                                  if (writer) {
                                    setSelectedWriter(writer);
                                    setPaymentAmount(writer.pending_earnings || 0);
                                    setPaymentDialogOpen(true);
                                  }
                                }}
                              >
                                Pay ${payment.pending_amount.toFixed(2)}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </Grid>

            {/* Payment Summary Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Payment Summary"
                  subheader="Real-time earnings data from writer profiles"
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="h4" color="warning.dark">
                          ${writers.reduce((acc, w) => acc + (w.pending_earnings || 0), 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="warning.dark">
                          Total Pending
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="h4" color="success.dark">
                          ${writers.reduce((acc, w) => acc + (w.paid_earnings || 0), 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="success.dark">
                          Total Paid
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="h5" color="primary.dark" align="center">
                          ${writers.reduce((acc, w) => acc + (w.total_earnings || 0), 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="primary.dark" align="center">
                          Total Earnings (All Writers)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Top Earners Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Top Earners"
                  subheader="Writers with highest paid earnings"
                />
                <CardContent>
                  {writers
                    .filter(w => w.paid_earnings > 0)
                    .sort((a, b) => b.paid_earnings - a.paid_earnings)
                    .slice(0, 5)
                    .map((writer, index) => (
                      <Box key={writer.id} sx={{ mb: 2, p: 1, borderBottom: '1px solid #eee' }}>
                        <Grid container alignItems="center">
                          <Grid item xs={1}>
                            <Typography variant="h6" color="text.secondary">
                              #{index + 1}
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography variant="subtitle2" noWrap>
                              {writer.full_name || writer.user?.username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{writer.user?.username}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" color="success.main">
                              ${writer.paid_earnings.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ${writer.pending_earnings.toFixed(2)} pending
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  
                  {writers.filter(w => w.paid_earnings > 0).length === 0 && (
                    <Alert severity="info">
                      No payment history found. Payments will appear here after they are processed.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 3: Performance Analytics */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Performance Analytics (Real Data from Writer Profiles)
          </Typography>
          
          {/* Real Metrics Grid */}
          <Grid container spacing={3}>
            {/* Rating Statistics */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Star color="warning" sx={{ mr: 1 }} />
                    <Typography color="text.secondary">
                      Average Rating
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {(() => {
                      const ratedWriters = writers.filter(w => w.average_rating > 0);
                      if (ratedWriters.length === 0) return '0.00';
                      const total = ratedWriters.reduce((acc, w) => acc + w.average_rating, 0);
                      return (total / ratedWriters.length).toFixed(2);
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on {writers.reduce((acc, w) => acc + (w.total_ratings || 0), 0)} ratings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Orders Statistics */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Assignment color="primary" sx={{ mr: 1 }} />
                    <Typography color="text.secondary">
                      Total Orders Completed
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {writers.reduce((acc, w) => acc + (w.total_orders_completed || 0), 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Across all writers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Earnings Statistics */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AttachMoney color="success" sx={{ mr: 1 }} />
                    <Typography color="text.secondary">
                      Total Earnings
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    ${writers.reduce((acc, w) => acc + (w.total_earnings || 0), 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${writers.reduce((acc, w) => acc + (w.paid_earnings || 0), 0).toFixed(2)} paid • 
                    ${writers.reduce((acc, w) => acc + (w.pending_earnings || 0), 0).toFixed(2)} pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* On-time Delivery */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Timer color="info" sx={{ mr: 1 }} />
                    <Typography color="text.secondary">
                      Avg. On-time Rate
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {(() => {
                      const writersWithRate = writers.filter(w => w.on_time_delivery_rate > 0);
                      if (writersWithRate.length === 0) return '0%';
                      const total = writersWithRate.reduce((acc, w) => acc + w.on_time_delivery_rate, 0);
                      return (total / writersWithRate.length).toFixed(1) + '%';
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Average delivery performance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Top Performers Table */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Writers (Based on Ratings)
            </Typography>
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Writer</TableCell>
                      <TableCell align="center">Rating</TableCell>
                      <TableCell align="center">Orders</TableCell>
                      <TableCell align="center">Earnings</TableCell>
                      <TableCell align="center">On-time %</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {writers
                      .filter(w => w.average_rating > 0)
                      .sort((a, b) => b.average_rating - a.average_rating)
                      .slice(0, 10)
                      .map((writer) => (
                        <TableRow key={writer.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                {writer.first_name?.[0] || writer.user?.username?.[0] || 'W'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {writer.full_name || writer.user?.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{writer.user?.username}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Star fontSize="small" color="warning" />
                              <Typography sx={{ ml: 0.5 }}>
                                {writer.average_rating.toFixed(1)}
                              </Typography>
                              {writer.total_ratings > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({writer.total_ratings})
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography>
                              {writer.total_orders_completed || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              completed
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography color="success.main" fontWeight="bold">
                              ${(writer.total_earnings || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ${(writer.paid_earnings || 0).toFixed(2)} paid
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography>
                              {(writer.on_time_delivery_rate || 0).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={writer.account_status}
                              color={writer.account_status === 'APPROVED' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </TabPanel>
      </Paper>

      {/* View Writer Dialog - EXPANDED VERSION */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        {selectedWriter && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                  {selectedWriter.first_name?.[0] || selectedWriter.user?.username?.[0] || 'W'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {selectedWriter.first_name && selectedWriter.last_name 
                      ? `${selectedWriter.first_name} ${selectedWriter.last_name}`
                      : selectedWriter.user?.username || 'N/A'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedWriter.user?.email || 'No email'} • ID: {selectedWriter.id}
                  </Typography>
                </Box>
                <Chip
                  label={getStatusText(selectedWriter.account_status)}
                  color={getStatusColor(selectedWriter.account_status)}
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              {/* Earnings Summary */}
              <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <DataCard 
                        title="Pending Earnings" 
                        value={`$${(selectedWriter.pending_earnings || 0).toFixed(2)}`}
                        icon={AttachMoney}
                        color="warning"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DataCard 
                        title="Paid Earnings" 
                        value={`$${(selectedWriter.paid_earnings || 0).toFixed(2)}`}
                        icon={MonetizationOn}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DataCard 
                        title="Total Earnings" 
                        value={`$${(selectedWriter.total_earnings || 0).toFixed(2)}`}
                        icon={AccountBalanceWallet}
                        color="primary"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Personal Information" icon={AccountCircle} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">First Name</Typography>
                    <Typography variant="body1">{selectedWriter.first_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Last Name</Typography>
                    <Typography variant="body1">{selectedWriter.last_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedWriter.user?.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedWriter.phone_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Country</Typography>
                    <Typography variant="body1">{selectedWriter.country || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">City</Typography>
                    <Typography variant="body1">{selectedWriter.city || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Professional Information */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Professional Information" icon={Work} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Professional Title</Typography>
                    <Typography variant="body1">{selectedWriter.professional_title || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Bio</Typography>
                    <Typography variant="body1">{selectedWriter.bio || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Years of Experience</Typography>
                    <Typography variant="body1">{selectedWriter.years_of_experience || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Specialties</Typography>
                    {renderArrayAsChips(selectedWriter.specialties)}
                  </Grid>
                </Grid>
              </Box>

              {/* Education */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Education" icon={School} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Highest Degree</Typography>
                    <Typography variant="body1">{selectedWriter.highest_degree || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Field of Study</Typography>
                    <Typography variant="body1">{selectedWriter.field_of_study || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">University</Typography>
                    <Typography variant="body1">{selectedWriter.university || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Graduation Year</Typography>
                    <Typography variant="body1">{selectedWriter.graduation_year || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Writing Experience */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Writing Experience" icon={Create} />
                <Typography variant="body2" color="text.secondary">Experience Details</Typography>
                <Typography variant="body1" paragraph>
                  {selectedWriter.writing_experience || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Previous Platforms</Typography>
                <Typography variant="body1">
                  {selectedWriter.previous_platforms || 'N/A'}
                </Typography>
              </Box>

              {/* Preferences */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Work Preferences" icon={Settings} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Preferred Subjects</Typography>
                    {renderArrayAsChips(selectedWriter.preferred_subjects)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Preferred Academic Levels</Typography>
                    {renderArrayAsChips(selectedWriter.preferred_academic_levels)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Preferred Languages</Typography>
                    {renderArrayAsChips(selectedWriter.preferred_languages)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Hours per Week</Typography>
                    <Typography variant="body1">{selectedWriter.hours_per_week || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Preferred Deadline Type</Typography>
                    <Typography variant="body1">{selectedWriter.preferred_deadline_type || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Performance Metrics */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader title="Performance Metrics" icon={TrendingUp} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <DataCard 
                      title="Average Rating" 
                      value={selectedWriter.average_rating?.toFixed(1) || '0.0'}
                      icon={Star}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DataCard 
                      title="Total Orders" 
                      value={selectedWriter.total_orders_completed || 0}
                      icon={Assignment}
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DataCard 
                      title="On-time Rate" 
                      value={`${(selectedWriter.on_time_delivery_rate || 0).toFixed(1)}%`}
                      icon={Timer}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DataCard 
                      title="Success Rate" 
                      value={`${(selectedWriter.success_rate || 0).toFixed(1)}%`}
                      icon={CheckCircle}
                      color="success"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Orders Summary */}
              {writerOrders.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <SectionHeader title="Recent Orders" icon={Assignment} />
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order ID</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell align="right">Pages</TableCell>
                          <TableCell align="right">Budget</TableCell>
                          <TableCell align="right">Writer Earning</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {writerOrders.slice(0, 5).map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>#{order.id}</TableCell>
                            <TableCell>{order.title}</TableCell>
                            <TableCell align="right">{order.pages}</TableCell>
                            <TableCell align="right">${order.budget.toFixed(2)}</TableCell>
                            <TableCell align="right">${order.writer_earning.toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={order.status}
                                size="small"
                                color={order.status === 'CLOSED' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {order.completed_at ? formatDate(order.completed_at) : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Showing {Math.min(5, writerOrders.length)} of {writerOrders.length} orders
                  </Typography>
                </Box>
              )}

              {/* Admin Section */}
              <Box>
                <SectionHeader title="Admin Information" icon={AdminPanelSettings} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Account Status</Typography>
                    <Chip
                      label={getStatusText(selectedWriter.account_status)}
                      color={getStatusColor(selectedWriter.account_status)}
                      sx={{ mb: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Account Created</Typography>
                    <Typography variant="body1">{formatDate(selectedWriter.account_created_at)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Application Submitted</Typography>
                    <Typography variant="body1">{formatDate(selectedWriter.application_submitted_at)}</Typography>
                  </Grid>
                  {selectedWriter.approved_at && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Approved At</Typography>
                      <Typography variant="body1">{formatDate(selectedWriter.approved_at)}</Typography>
                    </Grid>
                  )}
                  {selectedWriter.rejection_reason && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                      <Typography variant="body1" color="error">
                        {selectedWriter.rejection_reason}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Admin Notes</Typography>
                    <Typography variant="body1" paragraph>
                      {selectedWriter.admin_notes || 'No admin notes'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => {
                        setAdminNotes(selectedWriter.admin_notes || '');
                        setNotesDialogOpen(true);
                      }}
                    >
                      Edit Admin Notes
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
              
              {selectedWriter.account_status === 'PENDING' || selectedWriter.account_status === 'UNDER_REVIEW' ? (
                <>
                  <Button
                    color="success"
                    variant="contained"
                    startIcon={<Check />}
                    onClick={() => handleApproveWriter(selectedWriter)}
                  >
                    Approve
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRejectDialogOpen(true);
                    }}
                  >
                    Reject
                  </Button>
                </>
              ) : selectedWriter.account_status === 'APPROVED' ? (
                <Button
                  color="warning"
                  variant="outlined"
                  startIcon={<Block />}
                  onClick={() => handleSuspendWriter(selectedWriter)}
                >
                  Suspend
                </Button>
              ) : selectedWriter.account_status === 'SUSPENDED' ? (
                <Button
                  color="success"
                  variant="outlined"
                  startIcon={<CheckCircle />}
                  onClick={() => handleReactivateWriter(selectedWriter)}
                >
                  Reactivate
                </Button>
              ) : null}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Writer Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Please provide a reason for rejecting this writer application. This reason will be shared with the writer.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter detailed reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleRejectWriter(selectedWriter)}
          >
            Reject Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
      >
        <DialogTitle>
          Process Payment for {selectedWriter?.full_name || selectedWriter?.user?.username}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Processing payment for writer: {selectedWriter?.full_name || selectedWriter?.user?.username}
                <br />
                Current pending earnings: <strong>${(selectedWriter?.pending_earnings || 0).toFixed(2)}</strong>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Payment Amount"
                type="number"
                fullWidth
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                helperText={`Max: $${(selectedWriter?.pending_earnings || 0).toFixed(2)}`}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  label="Payment Method"
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Transaction ID"
                fullWidth
                value={`PAY-${selectedWriter?.id || '0'}-${Date.now()}`}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Add payment notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleProcessPayment}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Payment Dialog */}
      <Dialog
        open={bulkPaymentDialogOpen}
        onClose={() => setBulkPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bulk Process Payments ({selectedPayments.length} writers)
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to process payments for {selectedPayments.length} writers. 
                This will mark all their pending earnings as paid.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={bulkPaymentMethod}
                  label="Payment Method"
                  onChange={(e) => setBulkPaymentMethod(e.target.value)}
                >
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Transaction ID (Optional)"
                fullWidth
                value={bulkTransactionId}
                onChange={(e) => setBulkTransactionId(e.target.value)}
                placeholder="e.g., BULK-TXN-001"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Writers ({selectedPayments.length}):
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                {pendingPaymentsData
                  .filter(p => selectedPayments.includes(p.writer_id))
                  .map(payment => {
                    const writer = writers.find(w => w.id === payment.writer_id);
                    return (
                      <Typography key={payment.writer_id} variant="body2" sx={{ mb: 0.5 }}>
                        • {payment.writer_name} - ${payment.pending_amount.toFixed(2)}
                        {writer && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (Paid: ${writer.paid_earnings?.toFixed(2) || '0.00'})
                          </Typography>
                        )}
                      </Typography>
                    );
                  })}
              </Box>
              {selectedPayments.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
                  Total: ${pendingPaymentsData
                    .filter(p => selectedPayments.includes(p.writer_id))
                    .reduce((sum, p) => sum + p.pending_amount, 0)
                    .toFixed(2)}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPaymentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleBulkPaymentApprove}
            disabled={selectedPayments.length === 0}
          >
            Process Bulk Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
      >
        <DialogTitle>Edit Admin Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Notes"
            fullWidth
            multiline
            rows={6}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add internal notes about this writer..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleUpdateAdminNotes(selectedWriter)}
          >
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageWriters;